import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiChevronLeft, FiChevronRight, FiArrowRight } from 'react-icons/fi';
import '../styles/HeroSlider.css';

const HeroSlider = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);

  const slides = [
    {
      id: 1,
      image: '/images/hero-slide-a1.jpg',
      title: 'Supported Residential Accommodation',
      subtitle: 'Safe, nurturing care for vulnerable adults',
      description: 'Professional support tailored to individual needs',
      cta: 'Learn More',
    },
    {
      id: 2,
      image: '/images/hero-slide-2.jpg',
      title: 'Person-Centered Care',
      subtitle: 'Empowering independence and potential',
      description: 'Individualized care plans for every resident',
      cta: 'Our Services',
    },
    {
      id: 3,
      image: '/images/hero-slide-3.jpg',
      title: 'Professional Support 24/7',
      subtitle: 'Expert team dedicated to your wellbeing',
      description: 'Trained staff providing therapeutic care',
      cta: 'Meet Our Team',
    },
    {
      id: 4,
      image: '/images/hero-slide-4.jpg',
      title: 'Building Positive Futures',
      subtitle: 'Quality outcomes and life stability',
      description: 'Education, training, and employment support',
      cta: 'Start Journey',
    },
  ];

  useEffect(() => {
    if (!autoPlay) return;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [autoPlay, slides.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const slide = slides[currentSlide];

  const getQueueSlides = () => {
    return [
      slides[(currentSlide + 1) % slides.length],
      slides[(currentSlide + 2) % slides.length],
      slides[(currentSlide + 3) % slides.length],
    ];
  };

  const queueSlides = getQueueSlides();

  const bgImageVariants = {
    enter: { x: 600, opacity: 0 },
    center: {
      x: 0,
      opacity: 1,
      transition: { duration: 1, ease: 'easeInOut' },
    },
    exit: { x: -600, opacity: 0, transition: { duration: 1, ease: 'easeInOut' } },
  };

  const queueImageVariants = (index) => ({
    enter: { x: 200 + index * 80, opacity: 0, scale: 0.8 },
    center: {
      x: index * 80,
      opacity: 1,
      scale: 1,
      transition: { duration: 1, ease: 'easeInOut' },
    },
    exit: { x: -100, opacity: 0, scale: 0.8 },
  });

  const contentVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.1, duration: 0.6, ease: 'easeOut' },
    }),
  };

  return (
    <section className="hero-slider">
      {/* Background - Slides from right to left */}
      <div className="hero-background">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentSlide}
            className="background-image"
            variants={bgImageVariants}
            initial="enter"
            animate="center"
            exit="exit"
            style={{
              backgroundImage: `url(${slide.image})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
        </AnimatePresence>
        <div className="background-overlay" />
      </div>

      {/* Image Queue - Right Side (small thumbnails) */}
      <div className="image-queue">
        <AnimatePresence mode="wait">
          {queueSlides.map((qImg, idx) => (
            <motion.div
              key={`${currentSlide}-${idx}`}
              className="queue-image"
              variants={queueImageVariants(idx)}
              initial="enter"
              animate="center"
              exit="exit"
              style={{
                backgroundImage: `url(${qImg.image})`,
              }}
            />
          ))}
        </AnimatePresence>
      </div>

      {/* Content Box - Right Side */}
      <div className="hero-content">
        <motion.div
          className="content-wrapper"
          initial="hidden"
          animate="visible"
          key={currentSlide}
        >
          <motion.div className="content-badge" custom={0} variants={contentVariants}>
            Registration: 14464980
          </motion.div>

          <motion.h1 className="content-title" custom={1} variants={contentVariants}>
            {slide.title}
          </motion.h1>

          <motion.p className="content-subtitle" custom={2} variants={contentVariants}>
            {slide.subtitle}
          </motion.p>

          <motion.p className="content-description" custom={3} variants={contentVariants}>
            {slide.description}
          </motion.p>

          <motion.button
            className="content-cta"
            custom={4}
            variants={contentVariants}
            whileHover={{ scale: 1.05, boxShadow: '0 15px 35px rgba(52, 152, 219, 0.4)' }}
            whileTap={{ scale: 0.95 }}
          >
            {slide.cta} <FiArrowRight />
          </motion.button>

          <motion.div className="content-indicators" custom={5} variants={contentVariants}>
            {slides.map((_, index) => (
              <motion.button
                key={index}
                className={`indicator-dot ${index === currentSlide ? 'active' : ''}`}
                onClick={() => goToSlide(index)}
                animate={{
                  width: index === currentSlide ? '32px' : '10px',
                  backgroundColor:
                    index === currentSlide ? '#3498db' : 'rgba(255, 255, 255, 0.4)',
                }}
                transition={{ duration: 0.3 }}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </motion.div>
        </motion.div>
      </div>

      {/* Controls */}
      <div className="hero-controls">
        <motion.button
          className="control-arrow left-arrow"
          onClick={prevSlide}
          whileHover={{ scale: 1.15 }}
          whileTap={{ scale: 0.9 }}
          aria-label="Previous slide"
        >
          <FiChevronLeft size={28} />
        </motion.button>

        <motion.button
          className="control-arrow right-arrow"
          onClick={nextSlide}
          whileHover={{ scale: 1.15 }}
          whileTap={{ scale: 0.9 }}
          aria-label="Next slide"
        >
          <FiChevronRight size={28} />
        </motion.button>

        <div className="control-counter">
          <span>{String(currentSlide + 1).padStart(2, '0')}</span>
          <span>/</span>
          <span>{String(slides.length).padStart(2, '0')}</span>
        </div>

        <motion.button
          className={`control-autoplay ${autoPlay ? 'playing' : 'paused'}`}
          onClick={() => setAutoPlay(!autoPlay)}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          title={autoPlay ? 'Pause' : 'Play'}
        >
          <span className={`play-indicator ${autoPlay ? 'active' : ''}`} />
          <span>{autoPlay ? 'Playing' : 'Paused'}</span>
        </motion.button>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        className="scroll-indicator"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <div className="scroll-dot" />
      </motion.div>
    </section>
  );
};

export default HeroSlider;